/* eslint-disable max-lines-per-function */
import './CoralCampaign.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { Baseplate, StudRange } from '@websrc/App/Components/Baseplate/Baseplate'
import { BrickColor } from '@websrc/App/Components/Brick/Brick'
import { CTA } from '@websrc/App/Components/CTA'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCoralWebCampaign } from '@websrc/DataMappers/mapSpot'
import React, { FC, useContext, useMemo } from 'react'

type PropsCoralWebCampaign = ReturnType<typeof mapCoralWebCampaign>
const CartIcon: FC = () => (
  <svg
    className={'campaign__button-icon'}
    fill={'none'}
    height={'21'}
    viewBox={'0 0 21 21'}
    width={'21'}
    xmlns={'http://www.w3.org/2000/svg'}
  >
    <g id={'arrow-up-right-from-square'}>
      <path
        d={
          'M12.0156 3.46973C12.0156 3.06348 12.3281 2.71973 12.7656 2.71973H17.7344C18.1719 2.71973 18.4844 3.06348 18.4844 3.46973V8.46973C18.4844 8.90723 18.1719 9.21973 17.7344 9.21973C17.3281 9.21973 16.9844 8.90723 16.9844 8.46973V5.28223L10.0156 12.251C9.73438 12.5635 9.26562 12.5635 8.98438 12.251C8.67188 11.9697 8.67188 11.501 8.98438 11.1885L15.9531 4.21973H12.7656C12.3281 4.21973 12.0156 3.90723 12.0156 3.46973ZM4.76562 3.71973H8.76562C9.17188 3.71973 9.51562 4.06348 9.51562 4.46973C9.51562 4.90723 9.17188 5.21973 8.76562 5.21973H4.76562C4.32812 5.21973 4.01562 5.56348 4.01562 5.96973V16.4697C4.01562 16.9072 4.32812 17.2197 4.76562 17.2197H15.2656C15.6719 17.2197 16.0156 16.9072 16.0156 16.4697V12.4697C16.0156 12.0635 16.3281 11.7197 16.7656 11.7197C17.1719 11.7197 17.5156 12.0635 17.5156 12.4697V16.4697C17.5156 17.7197 16.4844 18.7197 15.2656 18.7197H4.76562C3.51562 18.7197 2.51562 17.7197 2.51562 16.4697V5.96973C2.51562 4.75098 3.51562 3.71973 4.76562 3.71973Z'
        }
        fill={'#141414'}
        id={'icon path'}
      />
    </g>
  </svg>
)
export const CoralCampaign: FC<PropsCoralWebCampaign> = ({
  video,
  image,
  image_alt_text,
  badge,
  label,
  header,
  subheader,
  button,
}) => {
  const { matomoPageTemplate } = useContext(AppProviderContext)

  const isMobile = !useIsWindowBiggerThan(719)
  const isTablet = useIsWindowBiggerThan(719)
  const isDesktop = useIsWindowBiggerThan(1079) //set to 1079 (and not 1023) to align with baseplate breakpoints
  const isCustomDesktop = useIsWindowBiggerThan(1229)
  const isWidescreen = useIsWindowBiggerThan(1439)
  const isCustomWidescreen = useIsWindowBiggerThan(1599)
  const isUltraWidescreen = useIsWindowBiggerThan(1759)

  const studRangeImage = useMemo((): StudRange[] => {
    if (isUltraWidescreen) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 24, columns: 2 },
          offset: { top: 0, bottom: 0, left: 2, right: 0 },
          bricks: [
            { row: 3, column: -1, color: BrickColor.Coral, snapPosition: 'top-left' },
            { row: 5, column: 0, color: BrickColor.Teal, snapPosition: 'top-left' },
          ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 24, columns: 2 },
          offset: { top: 0, bottom: 0, left: 0, right: 2 },
        },
      ]
    } else if (isCustomWidescreen) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 22, columns: 2 },
          offset: { top: 0, bottom: 0, left: 2, right: 0 },
          bricks: [
            { row: 3, column: -1, color: BrickColor.Coral, snapPosition: 'top-left' },
            { row: 5, column: 0, color: BrickColor.Teal, snapPosition: 'top-left' },
          ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 22, columns: 2 },
          offset: { top: 0, bottom: 0, left: 0, right: 2 },
        },
      ]
    } else if (isWidescreen) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 18, columns: 2 },
          offset: { top: 0, bottom: 0, left: 2, right: 0 },
          bricks: [
            { row: 3, column: -1, color: BrickColor.Coral, snapPosition: 'top-left' },
            { row: 5, column: 0, color: BrickColor.Teal, snapPosition: 'top-left' },
          ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 18, columns: 2 },
          offset: { top: 0, bottom: 0, left: 0, right: 2 },
        },
      ]
    } else if (isCustomDesktop) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 23, columns: 2 },
          offset: { top: 0, bottom: 0, left: 2, right: 0 },
          bricks: [
            { row: 3, column: -1, color: BrickColor.Coral, snapPosition: 'top-left' },
            { row: 5, column: 0, color: BrickColor.Teal, snapPosition: 'top-left' },
          ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 23, columns: 2 },
          offset: { top: 0, bottom: 0, left: 0, right: 2 },
        },
      ]
    } else if (isDesktop) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 20, columns: 2 },
          offset: { top: 0, bottom: 0, left: 2, right: 0 },
          bricks: [
            { row: 3, column: -1, color: BrickColor.Coral, snapPosition: 'top-left' },
            { row: 5, column: 0, color: BrickColor.Teal, snapPosition: 'top-left' },
          ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 20, columns: 2 },
          offset: { top: 0, bottom: 0, left: 0, right: 2 },
        },
      ]
    } else if (isTablet) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 32, columns: 1 },
          offset: { top: 0, bottom: 0, left: 1, right: 0 },
          bricks: [
            { row: 3, column: 0, color: BrickColor.Coral, snapPosition: 'top-left' },
            { row: 5, column: 0, color: BrickColor.Teal, snapPosition: 'top-left' },
          ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 32, columns: 1 },
          offset: { top: 0, bottom: 0, left: 0, right: 1 },
        },
      ]
    } else if (isMobile) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 32, columns: 1 },
          offset: { top: 0, bottom: 0, left: 1, right: 0 },
          bricks: [
            { row: 3, column: 0, color: BrickColor.Coral, snapPosition: 'top-left' },
            { row: 5, column: 0, color: BrickColor.Teal, snapPosition: 'top-left' },
          ],
        },
        {
          snapPosition: 'top-right',
          size: { rows: 32, columns: 1 },
          offset: { top: 0, bottom: 0, left: 0, right: 1 },
        },
      ]
    } else {
      return []
    }
  }, [
    isMobile,
    isTablet,
    isCustomDesktop,
    isDesktop,
    isWidescreen,
    isCustomWidescreen,
    isUltraWidescreen,
  ])

  const baseplateImageStyle = useMemo(() => {
    if (isDesktop) {
      return { width: 'fit-content' }
    }
  }, [isDesktop])

  return (
    <div className={'campaign'}>
      {video?.video && (
        <Asset
          altText={video.altText}
          className={'campaign__video'}
          loopAnimation={false}
          objectFit={'cover'}
          video={video?.video}
        />
      )}
      <Baseplate studRanges={studRangeImage} style={baseplateImageStyle}>
        <div className={'campaign__content'}>
          <CTA
            className={'campaign__image-button'}
            cta={button}
            onClick={() =>
              matomo.trackEvent(
                'conversion',
                matomoPageTemplate,
                ['coral campaign'],
                ['coral call to action image'],
                'button'
              )
            }
            type={'clean'}
          />
          {image && (
            <div className={'campaign__image-wrapper'}>
              <Asset
                altText={image_alt_text}
                className={'campaign__image'}
                objectFit={'cover'}
                thumbnail={image}
              />
            </div>
          )}
          <div className={'campaign__content-wrapper'}>
            <p className={'campaign__badge'}>{badge}</p>
            <div className={'campaign__header-wrapper'}>
              <p className={'campaign__label'}>{label}</p>
              <h2 className={'campaign__header'}>{header}</h2>
            </div>
            <p className={'campaign__subheader'}>{subheader}</p>
          </div>
          {button && (
            <CTA
              className={'campaign__button'}
              cta={button}
              onClick={() =>
                matomo.trackEvent(
                  'conversion',
                  matomoPageTemplate,
                  ['coral campaign'],
                  ['coral call to action button'],
                  'button'
                )
              }
              prefix={button.newTab ? <CartIcon /> : undefined}
              type={'secondary'}
            />
          )}
          <div className={'campaign__background'} />
        </div>
      </Baseplate>
    </div>
  )
}
